import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';

const CopyrightRegistration = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pricingPlans = [
    { name: "Basic", price: "$45", features: ["Copyright Registration", "Filing Assistance", "Copyright Certificate", "Email Support", "Standard Processing"] },
    { name: "Standard", price: "$149", popular: true, features: ["Everything in Basic", "Expedited Processing", "Attorney Review", "Priority Support", "Multiple Works", "Faster Approval"] },
    { name: "Premium", price: "$399", features: ["Everything in Standard", "Dedicated Attorney", "24/7 Premium Support", "Bulk Registration", "International Copyright", "Lifetime Protection"] }
  ];

  const process = [
    { step: "01", title: "Work Submission", description: "Submit your creative work and we'll prepare the copyright application with all required documentation." },
    { step: "02", title: "Filing & Registration", description: "We file your copyright application with the US Copyright Office following all federal requirements." },
    { step: "03", title: "Certificate Delivery", description: "Receive your official copyright registration certificate providing legal proof of ownership." }
  ];

  const testimonials = [
    { name: "Alex Martinez", role: "Author", text: "Protected my book with ease. Professional service from start to finish!" },
    { name: "Rachel Kim", role: "Photographer", text: "Fast copyright registration for my entire portfolio. Highly recommended!" },
    { name: "James Wilson", role: "Software Developer", text: "Excellent service for protecting my code. Very professional team." },
    { name: "Sophie Brown", role: "Musician", text: "Registered my music catalog quickly. Great customer support throughout." },
    { name: "Daniel Lee", role: "Graphic Designer", text: "Easy process for protecting my designs. Five-star experience!" }
  ];

  const faqs = [
    { question: "What can be copyrighted?", answer: "Original works including books, music, software, photographs, artwork, videos, and other creative content can be copyrighted." },
    { question: "How long does copyright registration take?", answer: "Standard copyright registration typically takes 3-6 months, while expedited processing can be completed in 1-2 weeks." },
    { question: "How long does copyright protection last?", answer: "Copyright protection lasts for the author's lifetime plus 70 years, or 95-120 years for corporate works." },
    { question: "Do I need to register to have copyright?", answer: "Copyright exists automatically upon creation, but registration provides legal benefits including the ability to sue for infringement and statutory damages." },
    { question: "Can I copyright multiple works at once?", answer: "Yes, you can register multiple related works together under certain circumstances, which can save time and money." }
  ];

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      <div className="pt-20 pb-16 bg-gradient-to-b from-[#131921] to-[#0F1419]">
        <div className="container mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
          </Link>
          <h1 className="text-5xl font-bold mb-6 text-[#FF9900]">US Copyright Registration</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Protect your creative works with official copyright registration. Starting at just $45.</p>
        </div>
      </div>
      <section className="py-20">
        <div className="container mx-auto px-6 text-center">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-purple-900 to-blue-900">
              <img src="https://images.unsplash.com/photo-1725347740938-73127ed5d91c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxjcmVhdGl2ZSUyMHdvcmslMjBhcnRpc3QlMjBkZXNpZ25lciUyMGlsbHVzdHJhdGlvbiUyMHZlY3RvcnxlbnwwfHx8fDE3NzUwNzI1MDl8MA&ixlib=rb-4.1.0&q=85" alt="Copyright" className="w-full h-full object-cover"/>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About Copyright Registration</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">Copyright protects original works of authorship including literary, artistic, musical, and software creations. Registration with the US Copyright Office provides legal proof of ownership and exclusive rights to reproduce, distribute, and display your work.</p>
              <p className="text-gray-300 mb-6 leading-relaxed">Our expert team handles the entire copyright registration process, ensuring your creative works are fully protected under federal law.</p>
              <Button 
                onClick={() => setIsContactFormOpen(true)}
                className="bg-[#FF9900] hover:bg-orange-600 text-white"
              >Get Started Today</Button>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-center mb-16">Our Simple 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, i) => (
              <div key={i} className="bg-[#0F1419] p-8 rounded-2xl border border-white/10 hover:border-[#FF9900]/50 transition-all">
                <div className="text-6xl font-bold text-[#FF9900] mb-4 opacity-20">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-center mb-4">Affordable Pricing Plans</h2>
          <p className="text-center text-gray-300 mb-16">Choose the plan that best fits your needs</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`relative bg-[#131921] p-8 rounded-2xl border-2 ${plan.popular ? 'border-[#FF9900]' : 'border-white/10'} hover:border-[#FF9900]/50 transition-all`}>
                {plan.popular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2"><span className="bg-[#FF9900] text-white px-4 py-1 rounded-full text-sm font-bold">MOST POPULAR</span></div>}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#FF9900] mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-start"><Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0"/><span className="text-gray-300">{f}</span></li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsContactFormOpen(true)}
                  className={`w-full ${plan.popular ? 'bg-[#FF9900] hover:bg-orange-600' : 'bg-white/10 hover:bg-white/20'} text-white`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6 text-center">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Our Copyright Services?</h2>
              <div className="space-y-4">
                {["Expert Copyright Specialists", "Fast Processing Times", "Affordable Rates", "Comprehensive Support"].map((title, i) => (
                  <div key={i} className="flex items-start">
                    <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0"/>
                    <div><h3 className="text-xl font-bold mb-2">{title}</h3><p className="text-gray-300">Professional copyright registration services with proven track record</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img src="/section3.png" alt="Why Choose" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-r from-[#FF9900] to-orange-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Protect Your Creative Work?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Join thousands of creators who have successfully registered their copyrights with our expert team.</p>
          <Button 
            onClick={() => setIsContactFormOpen(true)}
            className="bg-white text-[#FF9900] hover:bg-gray-100 text-lg px-8 py-6">Start Your Copyright Registration<ArrowRight className="ml-2 w-5 h-5"/></Button>
        </div>
      </section>
      <ServiceTestimonials 
        testimonials={testimonials}
        serviceName="Copyright Registration"
      />
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#0F1419] p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-[#FF9900]">{faq.question}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      
      <ContactFormDialog 
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
};

export default CopyrightRegistration;
